"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { DISTRICTS, DISTRICT_CODES, DISTRICT_SEGMENT } from "@/lib/districts";

export type ChipOption = { value: string; label: string };

export const BUDGET_OPTIONS: ChipOption[] = [
  { value: "", label: "Any budget" },
  { value: "u15", label: "Under S$1.5M" },
  { value: "15-2", label: "S$1.5M to S$2M" },
  { value: "2-3", label: "S$2M to S$3M" },
  { value: "3-4", label: "S$3M to S$4M" },
  { value: "4-6", label: "S$4M to S$6M" },
  { value: "o6", label: "Over S$6M" },
];

export const BEDS_OPTIONS: ChipOption[] = [
  { value: "", label: "Any bedrooms" },
  { value: "1", label: "1 bedroom" },
  { value: "2", label: "2 bedrooms" },
  { value: "3", label: "3 bedrooms" },
  { value: "4", label: "4 bedrooms" },
  { value: "5", label: "5 bedrooms or more" },
];

export const TOP_OPTIONS: ChipOption[] = [
  { value: "", label: "Any move-in year" },
  ...[2026, 2027, 2028, 2029, 2030, 2031].map((y) => ({ value: String(y), label: `TOP ${y}` })),
];

export const STATUS_OPTIONS: ChipOption[] = [
  { value: "", label: "Any stage" },
  { value: "prelaunch", label: "Pre-launch" },
  { value: "upcoming", label: "Upcoming" },
  { value: "selling", label: "Now selling" },
  { value: "completed", label: "Move in" },
  { value: "soldout", label: "Sold out" },
];

export const TENURE_OPTIONS: ChipOption[] = [
  { value: "", label: "Any tenure" },
  { value: "freehold", label: "Freehold" },
  { value: "99", label: "99-year" },
  { value: "999", label: "999-year" },
];

const SEGMENT_LABEL: Record<"CCR" | "RCR" | "OCR", string> = {
  CCR: "Core central",
  RCR: "Rest of central",
  OCR: "Outside central",
};

function useClickOutside(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);
  return ref;
}

function ChipShell({
  label,
  active,
  open,
  onToggle,
  children,
  wide,
  containerRef,
}: {
  label: string;
  active: boolean;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  wide?: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-full border px-4 py-2.5 text-left text-sm font-medium transition-colors",
          active
            ? "border-brand-500 bg-brand-50 text-brand-700"
            : "border-line bg-white text-ink hover:border-ink-faint"
        )}
      >
        <span className="truncate">{label}</span>
        <ChevronDown aria-hidden="true" className={cn("size-4 shrink-0 text-ink-faint transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div
          className={cn(
            "absolute left-0 z-30 mt-2 max-h-80 overflow-auto rounded-2xl border border-line bg-white p-2 shadow-card",
            wide ? "w-[min(92vw,42rem)]" : "w-64"
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/** Active-state chip for a picked place ("Near Hougang MRT") with a clear button. */
export function NearChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-2 rounded-full border border-brand-500 bg-brand-50 px-4 py-2.5 text-sm font-medium text-brand-700">
      <span className="truncate">Near {label}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear near ${label}`}
        className="flex size-5 shrink-0 items-center justify-center rounded-full text-brand-700 hover:bg-brand-100"
      >
        <X aria-hidden="true" className="size-3.5" />
      </button>
    </div>
  );
}

export function ChipSelect({
  options,
  value,
  onChange,
}: {
  options: ChipOption[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(open, () => setOpen(false));
  const current = options.find((o) => o.value === value) ?? options[0];
  return (
    <ChipShell label={current.label} active={!!value} open={open} onToggle={() => setOpen((v) => !v)} containerRef={ref}>
      <ul role="listbox">
        {options.map((o) => (
          <li key={o.value || "any"}>
            <button
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm hover:bg-surface",
                o.value === value ? "font-semibold text-brand-700" : "text-ink"
              )}
            >
              {o.label}
              {o.value === value && <Check aria-hidden="true" className="size-4" />}
            </button>
          </li>
        ))}
      </ul>
    </ChipShell>
  );
}

export function DistrictChip({
  value,
  onChange,
}: {
  value: string[];
  onChange: (codes: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(open, () => setOpen(false));
  const label =
    value.length === 0
      ? "Any district"
      : value.length === 1
      ? `${value[0]} · ${DISTRICTS[value[0]]?.label ?? ""}`
      : `${value.length} districts`;

  const toggle = (code: string) =>
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);

  return (
    <ChipShell label={label} active={value.length > 0} open={open} onToggle={() => setOpen((v) => !v)} wide containerRef={ref}>
      <div className="grid gap-4 p-2 sm:grid-cols-3">
        {(["CCR", "RCR", "OCR"] as const).map((seg) => (
          <div key={seg}>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">{SEGMENT_LABEL[seg]}</p>
            <ul className="space-y-0.5">
              {DISTRICT_CODES.filter((c) => DISTRICT_SEGMENT[c] === seg).map((code) => {
                const on = value.includes(code);
                return (
                  <li key={code}>
                    <button
                      type="button"
                      onClick={() => toggle(code)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-surface",
                        on ? "font-semibold text-brand-700" : "text-ink"
                      )}
                    >
                      <span
                        className={cn(
                          "flex size-4 shrink-0 items-center justify-center rounded border",
                          on ? "border-brand-500 bg-brand-500 text-white" : "border-line bg-white"
                        )}
                      >
                        {on && <Check aria-hidden="true" className="size-3" />}
                      </span>
                      <span className="w-8 shrink-0 font-semibold">{code}</span>
                      <span className="truncate text-ink-muted">{DISTRICTS[code]?.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between border-t border-line px-3 py-2">
        <button type="button" className="text-xs font-medium text-ink-muted hover:text-ink" onClick={() => onChange([])}>
          Clear
        </button>
        <button type="button" className="rounded-full bg-brand-500 px-4 py-1.5 text-xs font-semibold text-white" onClick={() => setOpen(false)}>
          Done
        </button>
      </div>
    </ChipShell>
  );
}
